import { css } from '@emotion/react';
import { FormControl, InputLabel, TextField, TextFieldProps, Typography } from '@mui/material';
import { ReactNode, useId } from 'react';

const labelCss = css`
  font-weight: 500;
  font-size: 0.8125rem;
  color: var(--mui-palette-text-primary);
  position: static;
  transform: none;
  margin-bottom: 4px;
  /* MUI's default InputLabel is absolute-positioned for the floating-label variant.
     Override so the label sits above the input and stays put. */
`;

const descriptionCss = css`
  margin-bottom: 4px;
`;

type LabeledTextFieldProps = Omit<TextFieldProps, 'label' | 'variant'> & {
  label: ReactNode;
  /** Caption-style description shown between the label and the input. */
  description?: ReactNode;
};

export function LabeledTextField({
  label,
  description,
  required,
  id,
  ...textFieldProps
}: LabeledTextFieldProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <FormControl fullWidth={textFieldProps.fullWidth ?? true} variant='outlined'>
      <InputLabel htmlFor={inputId} shrink={false} required={required} disableAnimation css={labelCss}>
        {label}
      </InputLabel>
      {description && (
        <Typography variant='caption' color='text.secondary' css={descriptionCss}>
          {description}
        </Typography>
      )}
      <TextField
        {...textFieldProps}
        id={inputId}
        required={required}
        label={undefined}
        variant='outlined'
        InputLabelProps={{ shrink: true, sx: { display: 'none' } }}
      />
    </FormControl>
  );
}
